import React from 'react'
import { Box, Typography, Avatar, Chip } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { FavoriteBorder, RemoveRedEyeOutlined, Folder, Image } from '@mui/icons-material'
import { Topic } from '@/types'
import { UserAvatar } from '../Avatar'
import { formattedNumber, getTopicNameFromTopic, pluralize } from '@/utils/helper'
import { TopicType, TopicTypes } from '@/utils/constants'

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`

const AlbumCardContainer = styled(Box)({
  position: 'relative',
  borderRadius: '20px',
  background: '#ffffff',
  border: '2px solid rgba(249, 115, 22, 0.12)',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: `
    0 4px 16px rgba(249, 115, 22, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.04)
  `,

  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: 'rgba(249, 115, 22, 0.3)',
    boxShadow: `
      0 20px 40px rgba(249, 115, 22, 0.2),
      0 8px 16px rgba(0, 0, 0, 0.08)
    `,

    '& .album-grid': {
      '& .photo': {
        transform: 'scale(1.05)',
      },
    },
  },
})

const AlbumHeader = styled(Box)({
  position: 'relative',
  width: '100%',
  height: '240px',
  background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
  overflow: 'hidden',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
})

// Polaroid-style photo grid
const PhotoGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: '8px',
  height: '100%',
  position: 'relative',
})

const PolaroidFrame = styled(Box)<{ delay?: number }>(({ delay = 0 }) => ({
  position: 'relative',
  background: '#ffffff',
  borderRadius: '8px',
  padding: '8px',
  paddingBottom: '24px',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease',
  animation: `${fadeIn} 0.5s ease ${delay}s both`,

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '4px',
    transition: 'transform 0.3s ease',
  },

  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '8px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '30px',
    height: '2px',
    background: 'rgba(249, 115, 22, 0.3)',
    borderRadius: '2px',
  },
}))

const AlbumBadge = styled(Box)({
  position: 'absolute',
  top: '16px',
  right: '16px',
  padding: '6px 12px',
  borderRadius: '10px',
  background: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
  backdropFilter: 'blur(10px)',
  border: '1.5px solid rgba(255, 255, 255, 0.3)',
  boxShadow: '0 4px 16px rgba(249, 115, 22, 0.4)',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  zIndex: 2,

  '& svg': {
    fontSize: '16px',
    color: '#ffffff',
  },

  '& span': {
    fontSize: '12px',
    fontWeight: 700,
    color: '#ffffff',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
})

const PhotoCountBadge = styled(Box)({
  position: 'absolute',
  bottom: '16px',
  left: '16px',
  padding: '8px 14px',
  borderRadius: '12px',
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '2px solid rgba(249, 115, 22, 0.2)',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  zIndex: 2,

  '& svg': {
    fontSize: '18px',
    color: '#f97316',
  },

  '& span': {
    fontSize: '14px',
    fontWeight: 700,
    color: '#0f172a',
  },
})

const CardContent = styled(Box)({
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
})

const AlbumTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.01em',
  lineHeight: 1.3,
  marginBottom: '4px',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
})

const CardFooter = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingTop: '12px',
  borderTop: '1px solid rgba(226, 232, 240, 0.6)',
})

const CreatorInfo = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
})

const CreatorAvatar = styled(Avatar)({
  width: '28px',
  height: '28px',
  border: '2px solid rgba(249, 115, 22, 0.2)',
})

const CreatorName = styled(Typography)({
  fontSize: '13px',
  fontWeight: 600,
  color: '#64748b',
})

const StatsContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
})

const StatBadge = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  color: '#94a3b8',
  fontSize: '13px',
  fontWeight: 600,

  '& svg': {
    fontSize: '16px',
  },
})

interface AlbumCardProps {
  topic: Topic
  onClick: (topic: Topic) => void
}

const AlbumCard: React.FC<AlbumCardProps> = ({ topic, onClick }) => {
  const likes = Number(topic.totalLikes) || 0
  const views = Number(topic.visitorVisits || 0) + Number(topic.ownerVisits || 0)
  const photoCount = Number(topic.photoCount) || 0

  // Get up to 4 different photos from the album
  const photos = React.useMemo(() => {
    const mainPhoto = topic.latestEgo?.photoAlbumURL || '/placeholder.png'
    const placeholder = '/placeholder.png'

    // If topic has photos array, use them
    if (topic.photos && topic.photos.length > 0) {
      const albumPhotos: string[] = []
      const allPhotoUrls: string[] = []

      // Try to get different photos
      for (let i = 0; i < topic.photos.length; i++) {
        const photo = topic.photos[i]
        // Try multiple URL properties to find unique images
        const urls = [photo.photoAlbumURL, photo.photoThumbURL, photo.photoFullURL].filter(Boolean) // Remove null/undefined

        allPhotoUrls.push(...urls)
      }

      // Get unique photo URLs
      const uniqueUrls = Array.from(new Set(allPhotoUrls))

      // Take up to 4 unique photos
      for (let i = 0; i < Math.min(4, uniqueUrls.length); i++) {
        albumPhotos.push(uniqueUrls[i])
      }

      // If we have only 1 unique photo, repeat it 4 times for a collage effect
      // If we have 2-3 photos, repeat them to fill 4 slots
      if (albumPhotos.length < 4 && albumPhotos.length > 0) {
        const originalPhotos = [...albumPhotos]
        let index = 0
        while (albumPhotos.length < 4) {
          albumPhotos.push(originalPhotos[index % originalPhotos.length])
          index++
        }
      }

      // If still no photos, use placeholders
      if (albumPhotos.length === 0) {
        return [mainPhoto, mainPhoto, mainPhoto, mainPhoto]
      }

      return albumPhotos
    }

    // Fallback: repeat the main photo 4 times
    return [mainPhoto, mainPhoto, mainPhoto, mainPhoto]
  }, [topic.photos, topic.latestEgo])

  return (
    <AlbumCardContainer className='album-card' onClick={() => onClick(topic)}>
      <AlbumHeader>
        <PhotoGrid className='album-grid'>
          {photos.map((photo, index) => (
            <PolaroidFrame key={index} className='polaroid-frame photo' delay={index * 0.1}>
              <img src={photo} alt={`Photo ${index + 1}`} loading='lazy' />
            </PolaroidFrame>
          ))}
        </PhotoGrid>

        <AlbumBadge>
          <Folder />
          <span>{TopicTypes[TopicType.ALBUM].title}</span>
        </AlbumBadge>

        {photoCount > 0 && (
          <PhotoCountBadge>
            <Image />
            <span>{pluralize('', photoCount, 'Photo', 'Photos')}</span>
          </PhotoCountBadge>
        )}
      </AlbumHeader>

      <CardContent>
        <AlbumTitle>{getTopicNameFromTopic(topic)}</AlbumTitle>

        <CardFooter>
          <CreatorInfo>
            <UserAvatar
              borderRadius={100}
              name={topic.userName}
              url={topic.profilePicThumbURL}
              size={28}
            />
            {/* <CreatorAvatar
              src={topic.profilePicThumbURL || '/placeholder.png'}
              alt={topic.userName || 'User'}
            /> */}
            <CreatorName>{topic.userName || 'Anonymous'}</CreatorName>
          </CreatorInfo>

          <StatsContainer>
            <StatBadge>
              <FavoriteBorder />
              <span>{formattedNumber(likes)}</span>
            </StatBadge>
            <StatBadge>
              <RemoveRedEyeOutlined />
              <span>{formattedNumber(views)}</span>
            </StatBadge>
          </StatsContainer>
        </CardFooter>
      </CardContent>
    </AlbumCardContainer>
  )
}

export default AlbumCard
